import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { TrialProvider } from "./context/TrialContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TrialSignupPage from "./pages/TrialSignupPage";
import DashboardPage from "./pages/DashboardPage";
import BIAPage from "./pages/BIAPage";
import RiskPage from "./pages/RiskPage";
import PlansPage from "./pages/PlansPage";
import BillingPage from "./pages/BillingPage";
import SettingsPage from "./pages/SettingsPage";
import BIASettingsPage from "./pages/BIASettingsPage";
import BIAConfigurationPage from "./pages/BIAConfigurationPage";
import GuidePageNew from "./pages/GuidePageNew";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminGuidebooksPage from "./pages/AdminGuidebooksPage";
import GuidebookViewerPage from "./pages/GuidebookViewerPage";
import AdminAssetProfilesPage from "./pages/AdminAssetProfilesPage";
import AdminSnapshotsPage from "./pages/AdminSnapshotsPage";
import SnapshotViewerPage from "./pages/SnapshotViewerPage";
// ── V22.6 Mandates ─────────────────────────────────────────────
import ContactPage from "./pages/ContactPage";
import ProfileUpdatePage from "./pages/ProfileUpdatePage";
import ExercisesPage from "./pages/ExercisesPage";
import PoliciesPage from "./pages/PoliciesPage";
import OrgManagementPage from "./pages/OrgManagementPage";
import ServiceLineManagementPage from "./pages/ServiceLineManagementPage";
import ReportsPage from "./pages/ReportsPage";
import AuditReadinessDashboard from "./pages/AuditReadinessDashboard";
import AuditLogsPage from "./pages/AuditLogsPage";
import GovernancePage from "./pages/GovernancePage";
import OperationalBIAPage from "./pages/OperationalBIAPage";
import BIAReportPage from "./pages/BIAReportPage";
import RecoveryPlansPage from "./pages/RecoveryPlansPage";
import RiskAssessmentPage from "./pages/RiskAssessmentPage";
import BCMPolicyWizardPage from "./pages/BCMPolicyWizardPage";
import TrainingAwarenessPage from "./pages/TrainingAwarenessPage";
import CrisisCommunicationPage from "./pages/CrisisCommunicationPage";
import ComplianceReportPage from "./pages/ComplianceReportPage";
import RecoveryStrategyPage from "./pages/RecoveryStrategyPage";
import DocumentManagementPage from "./pages/DocumentManagementPage";
import ImprovementActionsPage from "./pages/ImprovementActionsPage";
import PACAPage from "./pages/PACAPage";
import ManagementReviewPage from "./pages/ManagementReviewPage";
import AssetInventoryPage from "./pages/AssetInventoryPage";
import ThreatProfilingPage from "./pages/ThreatProfilingPage";
import StakeholderRegisterPage from "./pages/StakeholderRegisterPage";
import InternalAuditPage from "./pages/InternalAuditPage";
import KPIDashboardPage from "./pages/KPIDashboardPage";
import ComplianceSummaryReport from "./pages/ComplianceSummaryReport";
import AuditPreparationChecklist from "./pages/AuditPreparationChecklist";
import ProcessOwnerRecoveryWizard from "./pages/ProcessOwnerRecoveryWizard";
import MyRecoveryPackagesPage from "./pages/MyRecoveryPackagesPage";
import RecoveryPackageReviewPage from "./pages/RecoveryPackageReviewPage";
import RecoveryReviewPage from "./pages/RecoveryReviewPage";
import MyRecoveryWorkPage from "./pages/MyRecoveryWorkPage";
import RecoveryExerciseTestingPage from "./pages/RecoveryExerciseTestingPage";
import ITDRPAdminDashboard from "./pages/ITDRPAdminDashboard";
import ApplicationProfileWizard from "./pages/ApplicationProfileWizard";
import ApplicationProfilesListPage from "./pages/ApplicationProfilesListPage";
import MyApplicationProfilesPage from "./pages/MyApplicationProfilesPage";
import ComplianceDashboardPage from "./pages/ComplianceDashboardPage";
import LessonsLearnedPage from "./pages/LessonsLearnedPage";
import MyExerciseTestsPage from "./pages/MyExerciseTestsPage";
import ExerciseTestReviewPage from "./pages/ExerciseTestReviewPage";
import ExerciseCalendarPage from "./pages/ExerciseCalendarPage";
import RAMyTasksPage from "./pages/RAMyTasksPage";
import RATrackerPage from "./pages/RATrackerPage";
import RADashboardPage from "./pages/RADashboardPage";
import RiskInheritancePage from "./pages/RiskInheritancePage";
import GuidedBIAPage from "./pages/GuidedBIAPage";
import PolicyAcknowledgmentPage from "./pages/PolicyAcknowledgmentPage";
import PDCAQuestionsAdminPage from "./pages/PDCAQuestionsAdminPage";
import MyRecoveryTasksPage from "./pages/MyRecoveryTasksPage";
import RecoveryTaskReviewPage from "./pages/RecoveryTaskReviewPage";
import EmergencyResponsePage from "./pages/EmergencyResponsePage";
import PlanOrchestrationDashboard from "./pages/PlanOrchestrationDashboard";
import ITDRPWizardPage from "./pages/ITDRPWizardPage";
import RecoveryOrchestrationPage from "./pages/RecoveryOrchestrationPage";
import OrganizationSetupPage from "./pages/OrganizationSetupPage";
import MyPlanReviewsPage from "./pages/MyPlanReviewsPage";
import TrialAdminDashboard from "./pages/TrialAdminDashboard";
import PerformanceCompliancePage from "./pages/PerformanceCompliancePage";
import OrchestrationCenterPage from "./pages/OrchestrationCenterPage";
import MyWorkPage from "./pages/MyWorkPage";
import ForcePasswordResetPage from "./pages/ForcePasswordResetPage";
import AILabPage from "./pages/AILabPage";
import CyberRecoveryPlansPage from "./pages/CyberRecoveryPlansPage";
import SchedulerAdminPage from "./pages/SchedulerAdminPage";
import IdentityFederationPage from "./pages/IdentityFederationPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
// ── V22.9.9 Public-to-Private Conversion ────────────────────────
import DemoRequestPage from "./pages/DemoRequestPage";
import AdminDemoRequestsPage from "./pages/AdminDemoRequestsPage";
import DemoAccessGuard from "./components/DemoAccessGuard";

// Layout
import DashboardLayout from "./components/layout/DashboardLayout";
import OnboardingOverlay from "./components/OnboardingOverlay";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="loading-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force password reset for provisioned users
  if (user.must_reset_password) {
    return <ForcePasswordResetPage />;
  }
  
  return (
    <>
      <OnboardingOverlay />
      {children}
    </>
  );
};

// Subscription Check Route - trial removed; access governed by assigned plan (always active)
const SubscriptionRoute = ({ children }) => {
  const { user } = useAuth();

  // Only platform admin (WebAdmin) gets redirected to /billing on billing
  // issues — tenant users cannot access /billing, so redirect would loop.
  if (user && user.role === "admin" &&
      (user.subscription_status === "canceled" || user.subscription_status === "past_due")) {
    return <Navigate to="/billing" replace />;
  }

  return children;
};

// Block everyone except platform admin (WebAdmin role=admin). Tenant users
// (bcm_manager, department_head, process_manager, app_manager, etc.) and
// demo/sandbox users must never see /admin/*, /trial-admin, or /billing.
const AdminOnlyRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
};

// WebAdmin-only gate for pages that DO need to survive a full page reload
// (uses ProtectedRoute upstream for the loading/unauth handling; this only
// enforces the role once the user is resolved). Non-admins -> /dashboard.
const WebAdminGuard = ({ children }) => {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
};

// Catch-all: guidebook/blueprint HTML files were moved out of the public folder
// so their URLs (/<file>.html) now reach the SPA. Serve them ONLY to WebAdmin
// through the guarded GuidebookViewer; unauth -> /login, non-admin -> /dashboard
// (handled by ProtectedRoute + WebAdminGuard). Any other unknown path -> home.
const CatchAllRoute = () => {
  const isGuidebookHtml = /\.html$/i.test(window.location.pathname);
  if (isGuidebookHtml) {
    return (
      <ProtectedRoute>
        <WebAdminGuard>
          <GuidebookViewerPage />
        </WebAdminGuard>
      </ProtectedRoute>
    );
  }
  return <Navigate to="/" replace />;
};


// Mandate 1 — Mandatory Asset-Profile Initialization.
// Asset-owner roles get bounced to /profile-update on first login until
// they fill the high-fidelity profile. Admins / BCM Managers / Dept
// Heads are EXEMPT and proceed straight to /dashboard.
const PROFILE_REQUIRED_ROLES = new Set([
  "process_manager", "process_contributor",
  "app_manager", "application_manager", "app_contributor",
  "it_function_manager", "it_drp_manager", "it_function_contributor",
  "facility_manager", "facility_owner",
  "vendor_manager", "vendor_contact",
  "asset_owner",
]);
const PROFILE_EXEMPT_ROLES = new Set(["admin", "bcm_manager", "department_head"]);

const ProfileGate = ({ children }) => {
  const { user } = useAuth();
  if (!user) return children;
  if (PROFILE_EXEMPT_ROLES.has(user.role)) return children;
  if (!PROFILE_REQUIRED_ROLES.has(user.role)) return children;
  if (user.profile_completed) return children;
  return <Navigate to="/profile-update" replace />;
};

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      {/* Public Routes - Landing page always accessible */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/register" element={<Navigate to="/contact" replace />} />
      {/* Mandate 4 — /trial is now a redirect to /contact (no self-serve trial). */}
      <Route path="/trial" element={<Navigate to="/contact" replace />} />
      <Route path="/contact" element={<ContactPage />} />
      {/* V22.9.9 — Public-to-Private Conversion Protocol */}
      <Route path="/request-demo" element={<DemoRequestPage />} />
      <Route path="/demo/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      <Route path="/guide" element={<ProtectedRoute><WebAdminGuard><GuidePageNew /></WebAdminGuard></ProtectedRoute>} />
      {/* Mandate 1 — Mandatory Asset-Profile Initialization */}
      <Route path="/profile-update" element={
        <ProtectedRoute>
          <ProfileUpdatePage />
        </ProtectedRoute>
      } />

      {/* Protected Routes */}
      <Route path="/billing" element={
        <ProtectedRoute>
          <AdminOnlyRoute>
            <DashboardLayout>
              <BillingPage />
            </DashboardLayout>
          </AdminOnlyRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DemoAccessGuard>
              <ProfileGate>
                <DashboardLayout>
                  <DashboardPage />
                </DashboardLayout>
              </ProfileGate>
            </DemoAccessGuard>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      {/* V22.9.9 — Admin-only Demo Requests inbox */}
      <Route path="/admin/demo-requests" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <AdminDemoRequestsPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/bia" element={<Navigate to="/operational-bia" replace />} />
      
      <Route path="/risks" element={<Navigate to="/risk-assessment" replace />} />
      
      <Route path="/plans" element={<Navigate to="/recovery-plans" replace />} />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <SettingsPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/bia-settings" element={
        <Navigate to="/bia-configuration" replace />
      } />
      
      <Route path="/threat-profiling" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ThreatProfilingPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/bia-configuration" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <BIAConfigurationPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/exercises" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ExercisesPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/policies" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <PoliciesPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/policy-acknowledgment" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <PolicyAcknowledgmentPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/stakeholder-register" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <StakeholderRegisterPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/internal-audit" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <InternalAuditPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      
      <Route path="/org-management" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <OrgManagementPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/service-line-management" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ServiceLineManagementPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/organization-setup" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <OrganizationSetupPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ReportsPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/audit-readiness" element={<Navigate to="/performance-compliance?tab=audit" replace />} />
      
      <Route path="/audit-logs" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <AuditLogsPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/audit-preparation" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <AuditPreparationChecklist />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/governance" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <GovernancePage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/operational-bia" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <OperationalBIAPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/bia-report" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <BIAReportPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/recovery-plans" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RecoveryPlansPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/my-recovery-packages" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <MyRecoveryPackagesPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/recovery-wizard/bia/:biaId" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ProcessOwnerRecoveryWizard />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/recovery-wizard/package/:packageId" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ProcessOwnerRecoveryWizard />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/recovery-package-review" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RecoveryPackageReviewPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/recovery-package-review/:packageId" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RecoveryPackageReviewPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/my-recovery-tasks" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <MyRecoveryTasksPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/recovery-task-review" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RecoveryTaskReviewPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      
      <Route path="/emergency-response" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <EmergencyResponsePage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      
      {/* IT-DRP Plan Wizard */}
      <Route path="/it-drp-wizard" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ITDRPWizardPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      <Route path="/it-drp-wizard/:profileId" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ITDRPWizardPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      
      {/* Recovery Orchestration - redirected to Orchestration Center */}

      <Route path="/recovery-exercise-testing" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RecoveryExerciseTestingPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/recovery-exercise-testing/:packageId" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RecoveryExerciseTestingPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      
      <Route path="/exercise-test-review" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ExerciseTestReviewPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/exercise-calendar" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ExerciseCalendarPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/ra/my-tasks" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RAMyTasksPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/ra/tracker" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RATrackerPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/ra/dashboard" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RADashboardPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/risk-inheritance" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RiskInheritancePage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/guided-bia" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <GuidedBIAPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/try-demo" element={<Navigate to="/request-demo" replace />} />
      
      <Route path="/risk-assessment" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RiskAssessmentPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/bcm-policy" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <BCMPolicyWizardPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/training-awareness" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <TrainingAwarenessPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/crisis-communication" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <CrisisCommunicationPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/compliance-report" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ComplianceReportPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/compliance-summary" element={<Navigate to="/compliance-report" replace />} />
      
      <Route path="/asset-inventory" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <AssetInventoryPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/recovery-strategy" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <RecoveryStrategyPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/document-management" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <DocumentManagementPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/improvement-actions" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ImprovementActionsPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      <Route path="/pdca-integration" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ImprovementActionsPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/capa" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <PACAPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/management-review" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ManagementReviewPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminOnlyRoute>
            <AdminDashboardPage />
          </AdminOnlyRoute>
        </ProtectedRoute>
      } />

      <Route path="/admin/guidebooks" element={
        <ProtectedRoute>
          <AdminOnlyRoute>
            <AdminGuidebooksPage />
          </AdminOnlyRoute>
        </ProtectedRoute>
      } />

      <Route path="/admin/asset-profiles" element={
        <ProtectedRoute>
          <DashboardLayout>
            <AdminAssetProfilesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/snapshots" element={
        <ProtectedRoute>
          <AdminOnlyRoute>
            <AdminSnapshotsPage />
          </AdminOnlyRoute>
        </ProtectedRoute>
      } />

      {/* Public read-only snapshot viewer (no auth required) */}
      <Route path="/snapshot/:token" element={<SnapshotViewerPage />} />

      <Route path="/admin/pdca-questions" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <AdminOnlyRoute>
              <DashboardLayout>
                <PDCAQuestionsAdminPage />
              </DashboardLayout>
            </AdminOnlyRoute>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/it-drp-admin" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ITDRPAdminDashboard />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/application-profile/:taskId" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ApplicationProfileWizard />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/application-profiles" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <ApplicationProfilesListPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />


      <Route path="/lessons-learned" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <LessonsLearnedPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/trial-admin" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <AdminOnlyRoute>
              <DashboardLayout>
                <TrialAdminDashboard />
              </DashboardLayout>
            </AdminOnlyRoute>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      {/* Consolidated Pages */}
      <Route path="/performance-compliance" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <PerformanceCompliancePage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/orchestration-center" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <OrchestrationCenterPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/ai-lab" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <AILabPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/cyber-recovery" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <CyberRecoveryPlansPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/admin/scheduler" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            {["admin", "bcm_manager"].includes(user?.role) ? (
              <DashboardLayout>
                <SchedulerAdminPage />
              </DashboardLayout>
            ) : <Navigate to="/dashboard" replace />}
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/federation" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <IdentityFederationPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      <Route path="/my-work" element={
        <ProtectedRoute>
          <SubscriptionRoute>
            <DashboardLayout>
              <MyWorkPage />
            </DashboardLayout>
          </SubscriptionRoute>
        </ProtectedRoute>
      } />

      {/* Legacy route redirects to consolidated pages */}
      <Route path="/kpi-dashboard" element={<Navigate to="/performance-compliance" replace />} />
      <Route path="/compliance-dashboard" element={<Navigate to="/performance-compliance?tab=compliance" replace />} />
      <Route path="/recovery-orchestration" element={<Navigate to="/orchestration-center" replace />} />
      <Route path="/plan-orchestration" element={<Navigate to="/orchestration-center?tab=unit" replace />} />
      <Route path="/my-recovery-work" element={<Navigate to="/my-work" replace />} />
      <Route path="/my-plan-reviews" element={<Navigate to="/my-work?tab=reviews" replace />} />
      <Route path="/my-exercise-tests" element={<Navigate to="/my-work?tab=exercises" replace />} />
      <Route path="/my-application-profiles" element={<Navigate to="/my-work?tab=profiles" replace />} />
      <Route path="/recovery-review" element={<Navigate to="/recovery-plans?tab=overview" replace />} />
      
      {/* Dedicated WebAdmin guidebook viewer route (Library "Preview" links).
          Same guard pattern as /guide; resolves the :filename param. */}
      <Route
        path="/admin/guidebook-viewer/:filename"
        element={
          <ProtectedRoute>
            <WebAdminGuard>
              <GuidebookViewerPage />
            </WebAdminGuard>
          </ProtectedRoute>
        }
      />

      {/* Catch all — guidebook/blueprint HTML files (relocated out of /public)
          fall through to the SPA here. WebAdmin-only, via the same
          ProtectedRoute + WebAdminGuard pattern used by /guide. Everything
          else redirects home. */}
      <Route path="*" element={<CatchAllRoute />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TrialProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </TrialProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
